import {
  CalendarDays, Check, ChevronDown, ChevronsDownUp, ChevronsUpDown, ListFilter, Pencil, Plus, Search, X,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ErrorState, Skeleton, Spinner } from "@/components/ui/feedback";
import { Input, Textarea } from "@/components/ui/input";
import { UsPhoneInput } from "@/components/ui/phone-input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ReferencePicker, type ReferenceChoice } from "@/features/reference-picker";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STATE_CHOICES } from "@/lib/firms";
import { EARLIEST, LATEST, leadWindowProblem, windowOrigin, windowWords } from "@/lib/calling-window";
import { US_TIMEZONES } from "@/lib/campaign";
import { sameValues } from "@/lib/form";
import {
  useCallingWindow, useContactFields, useFirmFields, useUpdateContact, useUpdateContactFields, useUpdateFirmFields,
} from "@/lib/queries";
import {
  CONTACT_SECTION, DETAIL_LIMITS, type Draft, type FieldFilter, type Parsed, confirmsSave, contactFirst, detailEditable,
  detailInput, draftOf, editable, emptyText, fieldRows,
  filledCount, filterSections, latestDay, markedNeeded, markedTitle, maxLength, nothingShownText, panelFields,
  parseDetail, parseOwn, recordText, referenceLink, referenceText, refusalProblems, searchFolders, shownSections,
} from "@/lib/record-fields";
import { type ContractAsk, type ContractProblem, contractProblem } from "@/lib/send-contract";
import type { FormEntity, LeadCard, RecordField, RecordSection } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * A record's details panel (ACL-405; the fields are ACL-255's, ADR-087 in `platform`), laid out as
 * GoHighLevel's contact panel: a
 * title and a search over fields and folders, then a box per section an admin defined on the form, in
 * the form's order. Every field is changed where it is read: a click (or Enter) turns the
 * value into its input, Enter or leaving the field saves that one answer, Escape puts it back. There is
 * no drawer and no Edit button.
 *
 * What a person sees is what the server sent for their role: a field hidden from it is not in the
 * answer, and a folder left with nothing is not drawn. A field they may not change reads as text, with
 * no hover and no pencil. The server checks every save again; a refusal is said under the field, by
 * its title, and the field stays open with what was typed.
 *
 * A contact's panel is the record's one list of details, so it also draws the platform's own fields
 * (the name, the phone, the e-mail...) in the folder the form puts them, and saves them through the
 * contact itself (`PATCH /leads/{id}`), where a rep may change the names, the language, the state (a
 * list, ACL-576) and the e-mail, and a supervisor also the phone; the timezone is read only
 * (`detailEditable`). A firm's panel draws the firm's own fields only.
 *
 * The folders open and close; the first starts open, so the record's name and phone show at once. On
 * a contact's panel the Contact folder comes first whatever the form's order, is always open and does
 * not close, and the others start closed (ACL-526). A search opens the folders that match with only
 * the fields that do. Until `GET /leads/{id}/fields`
 * is deployed it answers 404, and nothing is drawn: the record's Identity card still names the contact.
 *
 * A reference (ACL-260) reads as the linked record's name, a link to it, and is picked from a search
 * the server runs; a contact the reader may not open is never named, as the server sends it.
 *
 * The contact page, the Conversations screen's Contact Details panel (ACL-455, `compact`) and a firm's
 * Fields tab draw this panel, so the record reads and changes the same wherever it is opened.
 *
 * `fallback` is what the Conversations screen knows of the contact from the thread: shown read-only, in
 * the same folders, while the record's fields are read, when they cannot be (a 404, an error, a sender
 * with no record), or when this role may see none of them.
 *
 * `ask` is what the Send contract dialog listed to fix, handed over when it closes (ACL-541): until then
 * the panel reads as it always does. Each value it names is outlined in the warning tone with what is
 * wrong said under it, its folder opens and the first is scrolled to; the mark goes as soon as that
 * value is filled in or changed.
 */
export function ContactDetailsPanel({
  contact, compact = false, wide = false, fallback, ask = null,
}: {
  /** The record, or null for a sender who is no contact: only `fallback` is shown. */
  contact: Pick<LeadCard, "id"> | null;
  /** Drawn in a narrow side column: no title, and Expand all beside the filter. */
  compact?: boolean;
  /** Drawn across the record's wide column (ACL-590): one card, a folder wide enough lays its fields out
   *  in columns, an open folder shows every field, empty or not, and what the record's Identity card
   *  shows (`IDENTITY_KEYS`) is not drawn again, unless Send contract asked for it. */
  wide?: boolean;
  fallback?: RecordSection[];
  /** The values Send contract was refused for, to fix on the record (`contractAsk`). */
  ask?: ContractAsk | null;
}) {
  const { user } = useAuth();
  const fields = useContactFields(contact?.id);
  const saveFields = useUpdateContactFields();
  const saveContact = useUpdateContact();
  const record = fields.data?.sections.some((s) => s.fields.length > 0) ?? false;
  if (fallback && (!contact || !record)) {
    return (
      <FieldsPanel
        key={contact?.id ?? "none"}
        entity="lead"
        title="Details"
        compact={compact}
        // The record's folders may still come: until they have, a search that finds nothing here says nothing.
        more={contact !== null && fields.isPending}
        fields={{ isPending: false, error: null, data: { sections: fallback }, refetch: () => undefined }}
        editing={THREAD_DETAILS}
      />
    );
  }
  if (!contact) return null;
  const editing: Editing = {
    can: (field) => editable(field) || detailEditable(field, user?.role),
    parse: (field, draft) => (field.system ? parseDetail(field, draft) : parseOwn(field, draft)),
    save: (field, value, done) => {
      if (!field.system) {
        void saveFields.mutateAsync({ id: contact.id, values: { [field.key]: value } }).then(done.onSuccess, done.onError);
        return;
      }
      // The body is typed key by key (`detailInput`); a detail the record does not change is never sent.
      const input = detailInput(field.key, value);
      if (!input) {
        done.onError(new Error("This is not changed on the record"));
        return;
      }
      void saveContact.mutateAsync({ id: contact.id, ...input }).then(done.onSuccess, done.onError);
    },
  };
  const drawn =
    wide && fields.data
      ? {
          ...fields,
          data: {
            sections: fields.data.sections.map((section) => ({
              ...section,
              fields: section.fields.filter((f) => !(f.system && IDENTITY_KEYS.has(f.key) && !ask?.has(f.key))),
            })),
          },
        }
      : fields;
  // Keyed by the contact: moving to another one starts a fresh panel, so nothing typed on one can be
  // saved onto the next.
  return (
    <FieldsPanel
      key={contact.id}
      entity="lead"
      title="Details"
      compact={compact}
      wide={wide}
      fields={drawn}
      withSystem
      editing={editing}
      ask={ask}
    />
  );
}

/** The details the record's Identity card shows, and changes through Edit (ACL-590). */
const IDENTITY_KEYS: ReadonlySet<string> = new Set(["first_name", "last_name", "email", "phone", "language", "timezone", "state"]);

/** The thread's own view of the contact: read, never changed here (they are changed on the record). */
const THREAD_DETAILS: Editing = {
  can: () => false,
  parse: () => ({ ok: false, problem: "Changed on the contact's record" }),
  save: (_field, _value, done) => done.onError(new Error("Changed on the contact's record")),
  readOnlyHint: "Changed on the contact's record",
};

/** A firm's own fields, as the contact's, for the Fields tab of the firm's page. A firm that has none
 *  says so there, since the tab is its only content. */
export function FirmDetailsPanel({ firmId }: { firmId: number }) {
  const fields = useFirmFields(firmId);
  const save = useUpdateFirmFields();
  const editing: Editing = {
    can: editable,
    parse: parseOwn,
    save: (field, value, done) =>
      void save.mutateAsync({ id: firmId, values: { [field.key]: value } }).then(done.onSuccess, done.onError),
  };
  return (
    <FieldsPanel
      key={firmId}
      entity="firm"
      title="Fields"
      fields={fields}
      editing={editing}
      emptyText="No custom fields for firms yet. An admin adds them under Settings, Custom fields."
      missingText="This firm's custom fields are not available yet."
    />
  );
}

/** When the contact likes to be called (ADR-156): set by a callback with a time, or here by
 *  anybody who may open the record, on their own clock. A value of the record's Identity card
 *  (ACL-590), under its label. */
export function CallingWindowLine({ contact }: { contact: LeadCard }) {
  const saved = contact.calling_window ?? null;
  const save = useCallingWindow();
  const toast = useToast();
  const [open, setOpen] = React.useState(false);
  const [start, setStart] = React.useState(saved?.start ?? "16:00");
  const [end, setEnd] = React.useState(saved?.end ?? "17:00");
  const problem = leadWindowProblem(start, end);
  const send = (next: { start: string; end: string } | null) =>
    save.mutate(
      { id: contact.id, window: next },
      {
        onSuccess: () => {
          setOpen(false);
          toast.success(next ? `Calling window ${windowWords(next)}` : "Calling window cleared");
        },
        onError: (e) => toast.error("Could not save the calling window", e instanceof ApiError ? e.message : undefined),
      },
    );
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 flex-1">
        {saved ? (
          <>
            Calls best {windowWords(saved)}
            <span className="text-gray-11"> · {windowOrigin(saved)}</span>
          </>
        ) : (
          <span className="text-text-subtle">Not set</span>
        )}
      </span>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (o) {
            setStart(saved?.start ?? "16:00");
            setEnd(saved?.end ?? "17:00");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="-my-1 -mr-2" aria-label="Edit the calling window">
            <Pencil />
            {saved ? "Edit" : "Set"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 space-y-2 p-3">
          <p className="text-xs text-text-muted">
            On their own clock. A cadence that starts its rounds in the window calls them then first; it is a preference, not a
            limit.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Input type="time" aria-label="From" min={EARLIEST} max={LATEST} step={900} value={start} onChange={(e) => setStart(e.target.value)} />
            <Input type="time" aria-label="Until" min={EARLIEST} max={LATEST} step={900} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          {problem && <p role="alert" className="text-2xs text-danger-11">{problem}</p>}
          <div className="flex justify-end gap-1.5">
            {saved && (
              <Button variant="ghost" size="sm" disabled={save.isPending} onClick={() => send(null)}>
                Clear
              </Button>
            )}
            <Button size="sm" disabled={problem !== null || save.isPending} onClick={() => send({ start, end })}>
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}

type Done = { onSuccess: () => void; onError: (e: unknown) => void };

/**
 * How a panel changes one field: whether this person may, what a draft sends, and where it goes. Each
 * save answers through its own promise (`mutateAsync`), not `mutate`'s callbacks, which React Query
 * fires for the latest call only: a second answer saved before the first came back would leave the
 * first one saving for good.
 */
interface Editing {
  can: (field: RecordField) => boolean;
  parse: (field: RecordField, draft: Draft) => Parsed;
  save: (field: RecordField, value: unknown, done: Done) => void;
  /** Told by a row when its input opens and closes (the panel keeps that row and its folder in view). */
  onActive?: (key: string, active: boolean) => void;
  /** Why a field that cannot be changed cannot, on hover; by default, the reader's role. */
  readOnlyHint?: string;
}

function FieldsPanel({
  entity, title, compact = false, wide = false, more = false, fields, withSystem = false, editing, emptyText: noneText,
  missingText, ask = null,
}: {
  /** What the record is: a contact (`lead`) or a firm. */
  entity: FormEntity;
  title: string;
  /** In a narrow side column under a heading of its own: the title is not drawn, and Expand all is an
   *  icon in the search box. */
  compact?: boolean;
  /** Across a wide column: the folders lay their fields out in columns once they are wide enough. */
  wide?: boolean;
  /** More fields are on their way: a search that finds nothing yet says nothing. */
  more?: boolean;
  fields: {
    isPending: boolean;
    error: unknown;
    data?: { sections: RecordSection[] };
    refetch: () => unknown;
  };
  /** Draw the platform's own fields too (a contact's panel). */
  withSystem?: boolean;
  editing: Editing;
  /** Said when there is nothing to show; without it, nothing is drawn. */
  emptyText?: string;
  /** Said when the server does not have the fields (a 404), by a tab that is nothing else; without it,
   *  nothing is drawn and the record looks as it did. */
  missingText?: string;
  /** What Send contract was refused for (ACL-541): marked on the fields that hold it. */
  ask?: ContractAsk | null;
}) {
  const [query, setQueryState] = React.useState("");
  const [filter, setFilterState] = React.useState<FieldFilter>("all");
  /** Fields changed since the search or the filter was set: shown whatever they now hold, so a field
   *  just answered does not vanish under the person's hand. */
  const [kept, setKept] = React.useState<ReadonlySet<string>>(() => new Set());
  /** Fields whose input is open (or saving): never hidden by the search or the filter, and their folder
   *  stays open, so an edit is never dropped unseen. */
  const [active, setActive] = React.useState<ReadonlySet<string>>(() => new Set());
  /** The folders a person closed while a search or a filter is on; the next search or filter starts
   *  with them all open again. */
  const [narrowClosed, setNarrowClosed] = React.useState<ReadonlySet<string>>(() => new Set());
  const narrowedNow = React.useRef(false);
  const setQuery = (next: string) => {
    narrowedNow.current = next.trim() !== "" || filter !== "all";
    setQueryState(next);
    setKept(new Set());
    setNarrowClosed(new Set());
  };
  const setFilter = (next: FieldFilter) => {
    narrowedNow.current = query.trim() !== "" || next !== "all";
    setFilterState(next);
    setKept(new Set());
    setNarrowClosed(new Set());
  };
  const onActive = React.useCallback((key: string, on: boolean) => {
    setActive((set) => {
      if (set.has(key) === on) return set;
      const next = new Set(set);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  /** The folders a person opened or closed; null until they do, when the first folder is open. */
  const [opened, setOpened] = React.useState<ReadonlySet<string> | null>(null);
  /** The refused Send contract whose folders were opened (ACL-541): each new one opens them once. */
  const [shownAsk, setShownAsk] = React.useState<ContractAsk | null>(null);
  const sectionRef = React.useRef<HTMLElement>(null);
  // Then the first value it names is scrolled to, once its folder has opened (the fields may still have
  // been on their way when it came).
  React.useEffect(() => {
    if (!shownAsk) return;
    sectionRef.current?.querySelector("[data-contract-problem]")?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [shownAsk]);
  /** Saves on their way, and whether two crossed: each answer is the whole record as that save left it,
   *  and the one that comes back last may not hold the other's change, so the record is read again. */
  const flight = React.useRef({ count: 0, crossed: false });
  const save: Editing["save"] = (field, value, done) => {
    const f = flight.current;
    if (f.count > 0) f.crossed = true;
    f.count += 1;
    const landed = () => {
      f.count -= 1;
      if (f.count === 0 && f.crossed) {
        f.crossed = false;
        void fields.refetch();
      }
    };
    editing.save(field, value, {
      onSuccess: () => {
        landed();
        if (narrowedNow.current) setKept((k) => new Set(k).add(field.key));
        done.onSuccess();
      },
      onError: (e) => {
        landed();
        done.onError(e);
      },
    });
  };

  if (fields.isPending) return <PanelLoading title={title} compact={compact} />;
  // A failed background read keeps what was loaded on screen (and any field being typed into); only a
  // first read that failed shows the error.
  if (fields.error && !fields.data) {
    if (fields.error instanceof ApiError && fields.error.status === 404) {
      return missingText ? <p className="text-sm text-text-muted">{missingText}</p> : null;
    }
    return (
      <Card>
        <CardHeader title={title} />
        <ErrorState
          error={fields.error as Error}
          title={`Could not load this ${entity === "firm" ? "firm" : "contact"}'s fields`}
          action={
            <Button variant="outline" size="sm" onClick={() => void fields.refetch()}>
              Try again
            </Button>
          }
        />
      </Card>
    );
  }

  const drawn = shownSections(fields.data?.sections ?? [], withSystem);
  // A contact's own folder comes first, whatever the form's order (ACL-526).
  const sections = entity === "lead" ? contactFirst(drawn) : drawn;
  if (sections.length === 0) {
    return noneText ? <p className="text-sm text-text-muted">{noneText}</p> : null;
  }
  /** The contact's own folder: always open, and its header opens and closes nothing. */
  const fixed = (section: RecordSection) => entity === "lead" && section.key === CONTACT_SECTION;
  /** The folders a person opens and closes. */
  const folders = sections.filter((s) => !fixed(s));
  // With the Contact folder open for good, the others start closed; without it, the first starts open.
  const open = opened ?? new Set(folders.length < sections.length ? [] : sections.slice(0, 1).map((s) => s.key));
  // A refused Send contract opens the folders holding what it named, during the render so they never
  // draw closed first; after that they open and close as the person says, and stay open once filled in.
  if (ask !== shownAsk) {
    setShownAsk(ask);
    const named = folders.filter((s) => s.fields.some((f) => ask?.has(f.key))).map((s) => s.key);
    if (named.length > 0) setOpened(new Set([...open, ...named]));
  }
  const searching = query.trim() !== "";
  const filtering = filter !== "all";
  // A search or a filter opens the folders it keeps, with only the fields it keeps.
  const narrowed = searching || filtering;
  const keep = active.size === 0 ? kept : new Set([...kept, ...active]);
  const shown = filterSections(searchFolders(sections, query, withSystem, keep), filter, withSystem, keep);
  const allOpen = folders.every((s) => open.has(s.key));
  const flip = (set: ReadonlySet<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };
  // While a search or a filter is on, a folder's header opens and closes it in that view only, and the
  // folders as the person left them come back when it is cleared.
  const pinned = (section: RecordSection) => section.fields.some((f) => active.has(f.key));
  // A folder holding a field being changed stays open, and a click on its header changes nothing, now
  // or later.
  const toggle = (section: RecordSection) => {
    if (fixed(section) || pinned(section)) return;
    if (narrowed) setNarrowClosed((set) => flip(set, section.key));
    else setOpened(() => flip(open, section.key));
  };
  const isOpen = (section: RecordSection) =>
    fixed(section) || pinned(section) || (narrowed ? !narrowClosed.has(section.key) : open.has(section.key));
  // In a side column Expand all is an icon in the search box, beside the clear and the filter. It opens
  // and closes the folders a person may: the Contact folder stays open.
  const folding = compact && folders.length > 1;
  const icons = 1 + (searching ? 1 : 0) + (folding ? 1 : 0);
  const expandAll = () => setOpened(new Set(folders.filter((s) => !allOpen || pinned(s)).map((s) => s.key)));
  // Nothing to fold when the Contact folder is the only one.
  const expand = folders.length > 0 && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="-my-1 -mr-2 text-text-muted"
      disabled={narrowed}
      onClick={expandAll}
    >
      {allOpen ? <ChevronsDownUp /> : <ChevronsUpDown />}
      {allOpen ? "Collapse all" : "Expand all"}
    </Button>
  );
  const search = (
    <Input
      leading={<Search />}
      trailing={
        <span className="flex items-center gap-0.5">
          {searching && (
            <button
              type="button"
              aria-label="Clear the search"
              className="rounded p-1 text-text-subtle hover:text-text"
              onClick={() => setQuery("")}
            >
              <X />
            </button>
          )}
          {folding && (
            <button
              type="button"
              aria-label={allOpen ? "Collapse all folders" : "Expand all folders"}
              title={allOpen ? "Collapse all" : "Expand all"}
              className="rounded p-1 text-text-subtle hover:text-text disabled:pointer-events-none disabled:opacity-50"
              disabled={narrowed}
              onClick={expandAll}
            >
              {allOpen ? <ChevronsDownUp /> : <ChevronsUpDown />}
            </button>
          )}
          <FilterMenu filter={filter} onChange={setFilter} />
        </span>
      }
      className={icons === 3 ? "pr-20" : icons === 2 ? "pr-14" : undefined}
      placeholder="Search fields and folders"
      aria-label="Search fields and folders"
      value={query}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape" && searching) {
          e.preventDefault();
          setQuery("");
        }
      }}
    />
  );
  // The filter in force, said in words and cleared in one click: a dot on an icon is easy to miss.
  const chip = filtering && (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded-full bg-accent-3 py-0.5 pl-2.5 pr-1.5 text-xs font-medium text-accent-11 hover:bg-accent-4"
      aria-label={`Clear the filter: ${FILTER_CHIPS[filter]}`}
      onClick={() => setFilter("all")}
    >
      {FILTER_CHIPS[filter]}
      <X className="size-3.5" aria-hidden />
    </button>
  );
  const list = shown.map((section) => {
    const whole = sections.find((s) => s.key === section.key) ?? section;
    return (
      <Folder
        key={section.key}
        entity={entity}
        section={section}
        filled={filledCount(whole, withSystem)}
        total={panelFields(whole, withSystem).length}
        withSystem={withSystem}
        open={isOpen(section)}
        fixed={fixed(section)}
        wide={wide}
        query={query}
        editing={{ ...editing, save, onActive }}
        ask={ask}
        onToggle={() => toggle(section)}
      />
    );
  });
  if (wide) {
    return (
      // Across the record (ACL-590), one card: its title, the search and Expand all in its head, and the
      // folders one under another inside it, a line between each.
      <section
        ref={sectionRef}
        aria-label={title}
        className="overflow-hidden rounded-lg border border-border bg-surface shadow-xs @container"
      >
        <div className="flex min-h-15 flex-wrap items-center gap-x-4 gap-y-2.5 border-b border-border px-5 py-3">
          <h2 className="mr-auto text-lg font-semibold text-text">{title}</h2>
          <div className="order-last w-full @xl:order-none @xl:w-72">{search}</div>
          {expand}
        </div>
        {chip && <div className="flex border-b border-border px-5 py-2">{chip}</div>}
        {shown.length === 0 ? (
          more ? null : <p className="px-5 py-6 text-center text-sm text-text-muted">{nothingShownText(query, filter)}</p>
        ) : (
          <div className="divide-y divide-border">{list}</div>
        )}
      </section>
    );
  }
  return (
    // No card around the panel: each folder is a box of its own, and a card's border beside every
    // folder's read as a double line. The title and the search sit on the page, as GoHighLevel's do,
    // and the folders line up with the search's edges.
    <section ref={sectionRef} aria-label={title}>
      <div className="space-y-3">
        {!compact && (
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-text">{title}</h2>
            {expand}
          </div>
        )}
        {search}
        {chip && <div className="flex">{chip}</div>}
      </div>
      {shown.length === 0 ? (
        more ? null : <p className="mt-4 rounded-lg border border-border bg-surface px-4 py-6 text-center text-sm text-text-muted shadow-sm">
          {nothingShownText(query, filter)}
        </p>
      ) : (
        <div className="mt-4 space-y-2.5">{list}</div>
      )}
    </section>
  );
}

/** The filter's two choices, in GoHighLevel's words. Picking the one in force again turns it off. */
const FILTER_LABELS: Record<Exclude<FieldFilter, "all">, string> = {
  filled: "Hide empty fields and folders",
  empty: "Show only empty fields and folders",
};

/** What the chip under the search says while a filter is on. */
const FILTER_CHIPS: Record<Exclude<FieldFilter, "all">, string> = {
  filled: "Empty fields hidden",
  empty: "Only empty fields",
};

/** The filter button inside the search box, as GoHighLevel's: a menu of the two choices, with the one
 *  in force ticked and the button marked while it is. */
function FilterMenu({ filter, onChange }: { filter: FieldFilter; onChange: (next: FieldFilter) => void }) {
  const on = filter !== "all";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={on ? `Filter fields: ${FILTER_LABELS[filter]}` : "Filter fields"}
          title="Filter fields"
          className={cn("relative rounded p-1 hover:text-text", on ? "text-accent-11" : "text-text-subtle")}
        >
          <ListFilter />
          {on && <span className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-accent-9" aria-hidden />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(Object.keys(FILTER_LABELS) as Exclude<FieldFilter, "all">[]).map((choice) => (
          <DropdownMenuCheckboxItem
            key={choice}
            checked={filter === choice}
            onCheckedChange={(checked) => onChange(checked ? choice : "all")}
          >
            {FILTER_LABELS[choice]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The panel's shape while the fields are read, so the cards under it do not jump down when it comes. */
function PanelLoading({ title, compact }: { title: string; compact: boolean }) {
  return (
    <section aria-label={title} aria-busy>
      <div className="space-y-3">
        {!compact && <h2 className="text-base font-semibold text-text">{title}</h2>}
        <Skeleton className="h-8 w-full rounded-md" />
      </div>
      <div className="mt-4 space-y-2.5">
        <span className="sr-only">Loading…</span>
        {/* Uneven widths read as folder names; equal bars read as a progress bar. */}
        {[34, 58, 30, 22, 40].map((width, i) => (
          <div key={i} className="flex h-12 items-center rounded-lg border border-border bg-surface-sunken/70 px-4 shadow-sm">
            <Skeleton className="h-3" style={{ width: `${width}%` }} />
          </div>
        ))}
      </div>
    </section>
  );
}

function Folder({
  entity, section, filled, total, withSystem, open, fixed = false, wide = false, query, editing,
  ask, onToggle,
}: {
  entity: FormEntity;
  /** The folder as the search left it: the fields to draw. */
  section: RecordSection;
  /** Counted over the whole folder, whatever the search keeps. */
  filled: number;
  total: number;
  withSystem: boolean;
  /** Whether the folder's fields are shown. */
  open: boolean;
  /** Always open (a contact's Contact folder, ACL-526): its header is a plain title, with no chevron. */
  fixed?: boolean;
  /** One of the record's card's folders (`FieldsPanel`): no box of its own, and columns of fields once
   *  the card is wide enough. */
  wide?: boolean;
  query: string;
  editing: Editing;
  /** What Send contract was refused for (ACL-541). */
  ask: ContractAsk | null;
  onToggle: () => void;
}) {
  // Unique to this mounted folder, so two panels on one page never share an id.
  const bodyId = React.useId();
  const drawn = panelFields(section, withSystem);
  const heading = (
    <>
      <span className={cn("min-w-0 flex-1 truncate text-text", wide ? "text-base font-semibold" : "text-base font-medium")}>
        <Match text={section.title} query={query} />
      </span>
      <span className={cn("shrink-0 tabular-nums text-text-muted", wide ? "text-sm" : "text-xs")} title={`${filled} of ${total} filled`}>
        {filled} of {total}
      </span>
    </>
  );
  // On the record's card (ACL-590) a folder is a row of the card, white, with no line under its head.
  const head = wide ? "min-h-13 px-5 py-3" : "bg-surface-sunken/70 px-4 py-3";
  return (
    // Its own box, as GoHighLevel's folders are, with a little depth to stand out from the panel. A
    // choice's menu opens in a layer of its own and a linked record's list grows the box, so clipping
    // to the rounded corners cuts nothing off.
    <section className={cn(!wide && "overflow-hidden rounded-lg border border-border bg-surface shadow-sm")}>
      <h3>
        {fixed ? (
          <div className={cn("flex w-full items-center gap-3", !wide && "border-b border-border", head)}>
            {heading}
          </div>
        ) : (
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-3 text-left transition-colors",
              head,
              // The app's focus outline, drawn inside: the box clips to its rounded corners.
              "hover:bg-surface-hover focus-visible:-outline-offset-2",
              open && !wide && "border-b border-border",
            )}
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={onToggle}
          >
            {heading}
            <ChevronDown
              className={cn("size-4 shrink-0 text-text-subtle transition-transform duration-150", open && "rotate-180")}
              aria-hidden
            />
          </button>
        )}
      </h3>
      {open && (
        <div id={bodyId}>
          {drawn.length > 0 &&
            (wide ? (
              // Wide (ACL-590), the pairs dissolve into two columns with a line between them, every field a
              // row of its own; a long text has the width to itself, between runs of the two columns, so the
              // line never runs through it.
              <div className="px-5 pb-2">
                {wideRuns(drawn).map((run) =>
                  run.length === 1 && run[0]!.type === "long_text" ? (
                    <FieldRow
                      key={run[0]!.key}
                      entity={entity}
                      field={run[0]!}
                      wide
                      query={query}
                      editing={editing}
                      contract={ask?.get(run[0]!.key)}
                    />
                  ) : (
                    <div
                      key={run[0]!.key}
                      className="grid @lg:relative @lg:grid-cols-2 @lg:gap-x-10 @lg:before:pointer-events-none @lg:before:absolute @lg:before:inset-y-1 @lg:before:left-1/2 @lg:before:w-px @lg:before:bg-border"
                    >
                      {fieldRows(run).map((row) =>
                        row.length === 2 ? (
                          <div key={row[0]!.key} className="grid grid-cols-2 gap-x-4 @lg:contents">
                            {row.map((field) => (
                              <FieldRow
                                key={field.key}
                                entity={entity}
                                field={field}
                                wide
                                query={query}
                                editing={editing}
                                contract={ask?.get(field.key)}
                              />
                            ))}
                          </div>
                        ) : (
                          <FieldRow
                            key={row[0]!.key}
                            entity={entity}
                            field={row[0]!}
                            wide
                            query={query}
                            editing={editing}
                            contract={ask?.get(row[0]!.key)}
                          />
                        ),
                      )}
                    </div>
                  ),
                )}
              </div>
            ) : (
              <div className="space-y-1 px-4 py-3">
                {fieldRows(drawn).map((row) =>
                  row.length === 2 ? (
                    <div key={row[0]!.key} className="grid grid-cols-2 gap-x-4">
                      {row.map((field) => (
                        <FieldRow
                          key={field.key}
                          entity={entity}
                          field={field}
                          query={query}
                          editing={editing}
                          contract={ask?.get(field.key)}
                        />
                      ))}
                    </div>
                  ) : (
                    <FieldRow
                      key={row[0]!.key}
                      entity={entity}
                      field={row[0]!}
                      query={query}
                      editing={editing}
                      contract={ask?.get(row[0]!.key)}
                    />
                  ),
                )}
              </div>
            ))}
        </div>
      )}
    </section>
  );
}

/** A folder's fields across the record, in the form's order: runs of fields for the two columns, and each
 *  long text a run of its own. */
function wideRuns(fields: readonly RecordField[]): RecordField[][] {
  const runs: RecordField[][] = [];
  for (const field of fields) {
    const last = runs[runs.length - 1];
    if (field.type === "long_text") runs.push([field]);
    else if (last && last[0]!.type !== "long_text") last.push(field);
    else runs.push([field]);
  }
  return runs;
}

/** The part of a title a search names, marked. */
function Match({ text, query }: { text: string; query: string }) {
  const needle = query.trim().toLowerCase();
  const at = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-sm bg-accent-3 text-inherit">{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  );
}

/** How long "saved" shows beside a value after its save. */
const SAVED_MS = 1500;

/** The keys that save a long text, as this computer names them. */
const SAVE_KEYS =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ Enter" : "Ctrl Enter";

/**
 * One field: its title over its value, and the value turns into its input where it stands. A text
 * saves on Enter or on leaving it, a long text on ⌘/Ctrl+Enter or on leaving it, a choice the moment it
 * is picked; several choices and a linked record have Save and Cancel. Escape puts the value back.
 */
function FieldRow({
  entity, field, wide = false, query, editing: how, contract,
}: {
  entity: FormEntity;
  field: RecordField;
  /** Across the record (ACL-590): the question on the left and the answer on the right, once the folder
   *  is wide enough and while the answer is not being typed (the input then takes the row's width). */
  wide?: boolean;
  query: string;
  editing: Editing;
  /** Send contract was refused for this value (ACL-541): outlined, with why under it, until it changes. */
  contract?: ContractProblem;
}) {
  const { user } = useAuth();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<Draft>("");
  /** The name of a reference picked here: the draft holds only its id. */
  const [picked, setPicked] = React.useState<string | undefined>();
  const [problem, setProblem] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  /** What a saved value reads as until the record's answer comes back with it (a detail saved through
   *  the contact reads the fields again), so the old value never flashes back. */
  const [after, setAfter] = React.useState<{ text: string | null; until: unknown } | null>(null);
  /** Whether the input is open and not put back: a blur after Escape, after a save or after the input
   *  closed must not save again. */
  const live = React.useRef(false);
  /** A save on its way: Enter and the blur that follows it are one save, not two. */
  const busy = React.useRef(false);
  /** The draft the input opened with. A save is sent only when the person changed it: a value the
   *  half-minute refresh brought in while the input stood open untouched is never written over. */
  const startDraft = React.useRef<Draft>("");
  /** The draft the server last refused: leaving the field again does not send it again. */
  const refused = React.useRef<Draft | null>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const valueRef = React.useRef<HTMLButtonElement>(null);
  const savedTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  React.useEffect(() => () => clearTimeout(savedTimer.current), []);
  // Whether the record still holds the value from before a save made here (the save's own text reads
  // until then). Strict on purpose: a value is a scalar or a list of option keys in the options' order
  // (fields.py), and `sameValues` would read false, "" and [] as null, so a cleared "No" kept showing.
  const stale = after !== null && JSON.stringify(field.value ?? null) === JSON.stringify(after.until ?? null);
  // Once the record's answer moves on from the value saved here, it is the answer that reads.
  React.useEffect(() => {
    if (after && !stale) setAfter(null);
  }, [after, stale]);

  // Unique to this mounted row (React.useId), so two panels on one page never share an id; the key in
  // it keeps the ids readable.
  const id = `${React.useId()}field-${field.key}`;
  const canEdit = how.can(field);
  const { onActive } = how;
  React.useEffect(() => {
    onActive?.(field.key, editing);
    return () => onActive?.(field.key, false);
  }, [onActive, field.key, editing]);

  /** Moves the focus to `target` on the next frame, unless the person has moved on to something else
   *  meanwhile: a save that lands late never pulls the focus out of the field being typed into. */
  const focusIfFree = (target: () => HTMLElement | null) =>
    requestAnimationFrame(() => {
      const now = document.activeElement;
      if (now && now !== document.body && !rowRef.current?.contains(now)) return;
      target()?.focus();
    });
  const text = stale ? after.text : recordText(field);

  const start = () => {
    live.current = true;
    refused.current = null;
    startDraft.current = draftOf(field);
    setDraft(startDraft.current);
    setPicked(undefined);
    setProblem(null);
    setEditing(true);
    // The input takes the focus: a text by itself (autoFocus), a list of choices or a record picker here.
    focusIfFree(() => document.getElementById(id));
  };
  const stop = (refocus: boolean) => {
    live.current = false;
    setEditing(false);
    setProblem(null);
    // Back to the value, for a keyboard that pressed Enter or Escape; a click elsewhere keeps its focus.
    if (refocus) focusIfFree(() => valueRef.current);
  };
  const cancel = (refocus = true) => stop(refocus);
  const commit = (next: Draft = draft, refocus = false) => {
    if (!live.current || busy.current) return;
    setDraft(next);
    if (sameValues(next, startDraft.current)) {
      stop(refocus);
      return;
    }
    if (!refocus && refused.current !== null && sameValues(next, refused.current)) return;
    const parsed = how.parse(field, next);
    if (!parsed.ok) {
      setProblem(parsed.problem);
      return;
    }
    busy.current = true;
    setSaving(true);
    setProblem(null);
    const before = field.value;
    how.save(field, parsed.value, {
      onSuccess: () => {
        busy.current = false;
        setSaving(false);
        setAfter({ text: recordText({ ...field, value: parsed.value }), until: before });
        stop(refocus);
        setSaved(true);
        clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setSaved(false), SAVED_MS);
      },
      onError: (e) => {
        busy.current = false;
        setSaving(false);
        refused.current = next;
        // Back into the input to put it right, for a save sent from the keyboard (a click elsewhere
        // keeps its focus).
        if (refocus) focusIfFree(() => document.getElementById(id));
        if (!(e instanceof ApiError)) {
          setProblem("Could not save. Please try again.");
          return;
        }
        const { problems, rest } = refusalProblems(e.message, [field]);
        setProblem(problems[field.key] ?? rest ?? e.message);
      },
    });
  };

  const link = field.type === "reference" ? referenceLink(field, user?.role) : null;
  const empty = text === null;
  const none = empty ? emptyText(field) : null;
  // Read off the value as drawn, so the mark goes the moment a save lands, before the record is read again.
  const contractSays = contractProblem(contract, text);
  // A long text keeps its question over it, across the folder.
  const side = wide && !editing && field.type !== "long_text";
  // Beside its question an e-mail has half a column: it breaks after the @, not wherever the line ends.
  const at = side && field.type === "email" && typeof text === "string" ? text.indexOf("@") : -1;
  const value =
    empty && none ? (
      // One line, even in a half-width cell.
      <span className="block truncate italic text-text-subtle">{none}</span>
    ) : empty ? (
      // A dash under the question; a screen reader hears the words, not "dash".
      <span className="text-text-subtle">
        <span aria-hidden>—</span>
        <span className="sr-only">Not answered</span>
      </span>
    ) : field.type === "reference" && !link ? (
      <span className="text-text-muted">{text}</span>
    ) : (
      // The answer a little bolder than its question, so the eye lands on it.
      <span
        className={cn(
          // On the record (ACL-590) as the Identity card's values read: no bolder than the question.
          wide ? "font-normal" : "font-medium",
          field.type === "long_text" && "whitespace-pre-wrap",
          (field.type === "email" || field.type === "text") && "break-words",
          field.type === "email" && (at > 0 ? "break-words" : "break-all"),
          field.type === "phone" && "font-mono",
        )}
        title={field.system && field.key === "timezone" && typeof field.value === "string" ? field.value : undefined}
      >
        {at > 0 && typeof text === "string" ? <>{text.slice(0, at + 1)}<wbr />{text.slice(at + 1)}</> : text}
      </span>
    );
  const chooses = field.type === "dropdown" || field.type === "boolean" || (field.system && field.key === "state");

  return (
    <div
      ref={rowRef}
      className={cn(
        "py-1",
        side && "@3xl:grid @3xl:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] @3xl:items-baseline @3xl:gap-x-4 @3xl:py-[3px]",
      )}
      data-contract-problem={contractSays ? "" : undefined}
    >
      <div id={`${id}-label`} className={cn("pb-0.5 text-xs text-text-muted", side && "@3xl:pb-0 @3xl:text-base @3xl:leading-5 @3xl:text-gray-11")}>
        <Match text={markedTitle(field.title)} query={query} />
        {markedNeeded(field) && (
          <span className="ml-0.5 text-danger-9" aria-hidden>
            *
          </span>
        )}
      </div>
      {editing ? (
        <div className="-mx-2.5 space-y-1">
          <FieldInput
            entity={entity}
            field={field}
            id={id}
            draft={draft}
            pickedLabel={picked}
            invalid={Boolean(problem)}
            disabled={saving}
            onChange={(next) => {
              setDraft(next);
              setProblem(null);
            }}
            onPick={(choice) => {
              setPicked(choice?.label ?? "");
              commit(choice ? String(choice.id) : "", true);
            }}
            inline={{ commit, cancel, refuse: setProblem }}
          />
          {problem ? (
            <p id={`${id}-error`} role="alert" className="px-2.5 text-xs text-danger-11">
              {problem}
            </p>
          ) : field.type === "long_text" && !saving ? (
            <p className="px-2.5 text-xs text-text-subtle">{SAVE_KEYS} to save · Esc to cancel</p>
          ) : null}
        </div>
      ) : canEdit && !link ? (
        // A marked value's outline stands 6px out, not the hover box's 10px: the first and last name side
        // by side would touch across the 16px between them. The text stays under its title either way.
        <div className={contractSays ? "-mx-1.5" : "-mx-2.5"}>
          <button
            ref={valueRef}
            type="button"
            // Named "Edit <title> <value>": a screen reader hears the answer, and voice control can say
            // what is on screen.
            aria-labelledby={`${id}-edit ${id}-label ${id}-value`}
            aria-describedby={contractSays ? `${id}-contract` : undefined}
            title={empty ? "Add an answer" : undefined}
            className={cn(
              "group flex min-h-8 w-full items-center gap-2 rounded-md border py-1 text-left text-sm text-text transition-colors",
              wide && "text-base @3xl:text-[15px]",
              contractSays
                ? "border-warning-9 bg-warning-2 px-1.5 hover:bg-warning-4"
                : "border-transparent px-2.5 hover:border-border hover:bg-surface-hover",
            )}
            onClick={start}
          >
            <span id={`${id}-edit`} className="sr-only">Edit</span>
            <span id={`${id}-value`} className="min-w-0 flex-1">{value}</span>
            <RowEnd saved={saved}>
              {empty ? (
                // A plus where an answer can be added; the pencil where one can be changed.
                <Plus className="size-3.5 text-accent-11" />
              ) : chooses ? (
                <ChevronDown className="size-3.5 text-text-subtle" />
              ) : field.type === "date" ? (
                <CalendarDays className="size-3.5 text-text-subtle" />
              ) : (
                <Pencil className="size-3.5 text-text-subtle" />
              )}
            </RowEnd>
          </button>
        </div>
      ) : canEdit && link ? (
        // A linked record's name is a link to it, so the pencil beside it is what changes it.
        <div
          className={cn(
            "group flex min-h-8 items-center gap-2 rounded-md py-1 text-sm",
            wide && "text-base @3xl:text-[15px]",
            contractSays
              ? "-mx-1.5 border border-warning-9 bg-warning-2 px-1.5 hover:bg-warning-4"
              : "-mx-2.5 px-2.5 hover:bg-surface-hover",
          )}
        >
          <Link to={link} className="min-w-0 flex-1 truncate hover:underline">
            {text}
          </Link>
          <button
            ref={valueRef}
            type="button"
            aria-label={`Edit ${field.title}`}
            className="rounded p-0.5 text-text-subtle hover:text-text"
            onClick={start}
          >
            <RowEnd saved={saved}>
              <Pencil className="size-3.5" />
            </RowEnd>
          </button>
        </div>
      ) : (
        <div
          className={cn(
            "flex min-h-8 items-center py-1 text-sm text-text",
            wide && "text-base @3xl:text-[15px]",
            // Outlined as an editable marked value is, so the text does not move.
            contractSays && "-mx-1.5 rounded-md border border-warning-9 bg-warning-2 px-1.5",
          )}
          title={canEdit ? undefined : (how.readOnlyHint ?? "Your role can read this field, not change it.")}
        >
          {link ? (
            <Link to={link} className="hover:underline">
              {text}
            </Link>
          ) : (
            value
          )}
        </div>
      )}
      {/* Why Send contract stopped here; while the value is being typed too, unless a save was refused. */}
      {contractSays && !(editing && problem) && (
        <p id={`${id}-contract`} className={cn("pt-1 text-xs font-medium text-warning-11", side && "@3xl:col-start-2")}>
          {contractSays}
        </p>
      )}
    </div>
  );
}

/** The end of an editable value: its pencil (or chevron) on hover and focus, and "saved" just after. */
function RowEnd({ saved, children }: { saved: boolean; children: React.ReactNode }) {
  if (saved) {
    return (
      <span className="flex shrink-0 items-center gap-1 text-xs text-success-11" role="status">
        <Check className="size-3.5" />
        Saved
      </span>
    );
  }
  return (
    <span
      className={cn(
        "shrink-0 opacity-0 transition-opacity group-hover:opacity-100",
        "group-focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100",
      )}
      aria-hidden
    >
      {children}
    </span>
  );
}

const NOT_SET = "__not_set__";

/** What an inline input does on its keys: save a draft, or put the value back (and take the focus back
 *  to it, for a keyboard; not for a click elsewhere, which keeps its focus). */
interface Inline {
  commit: (draft?: Draft, refocus?: boolean) => void;
  cancel: (refocus?: boolean) => void;
  /** Says what is wrong and keeps the input open (a date typed only in part). */
  refuse: (problem: string) => void;
}

/** A date and time typed only in part reads as "" to the page, which would clear the stored one. A date
 *  alone is picked from the month (ACL-577), never typed. */
function partDate(el: HTMLInputElement | HTMLTextAreaElement): string | null {
  if (!(el instanceof HTMLInputElement) || !el.validity?.badInput) return null;
  return "Pick a date and a time";
}

/**
 * One field's input, by its type, for a draft. `inline` makes it the value's own: it takes the focus,
 * saves on Enter (⌘/Ctrl+Enter for a long text) and on leaving it, a choice saves as it is picked,
 * and Escape cancels. The phone and the e-mail (`confirmsSave`) save only on Enter or Save, and are put
 * back when they are left.
 */
function FieldInput({
  entity, field, id, draft, pickedLabel, invalid, disabled, onChange, onPick, inline,
  "aria-describedby": describedBy,
}: {
  entity: FormEntity;
  field: RecordField;
  id: string;
  draft: Draft;
  /** A reference's name, once picked here. */
  pickedLabel?: string;
  invalid?: boolean;
  disabled?: boolean;
  onChange: (draft: Draft) => void;
  onPick: (choice: ReferenceChoice | null) => void;
  inline?: Inline;
  "aria-describedby"?: string;
}) {
  const locked = !inline && !editable(field);
  const text = Array.isArray(draft) ? "" : draft;
  const labelled = inline ? { "aria-labelledby": `${id}-label` } : {};
  /** The error under the field, linked to whichever control it has. */
  const describedById = describedBy ?? (inline && invalid ? `${id}-error` : undefined);
  /** Escape puts the value back, for a control whose own Escape (a list, a menu) has not taken it. */
  const escapeCancels = (e: React.KeyboardEvent) => {
    if (!inline || e.key !== "Escape" || e.defaultPrevented) return;
    e.preventDefault();
    inline.cancel();
  };
  /** Set when a choice is picked, so the menu closing after it is not read as a cancel. */
  const chosen = React.useRef(false);
  /** Set by a click outside the menu: the focus then stays where the person clicked. */
  const pointerOutside = React.useRef(false);

  const keys = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!inline) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      inline.cancel();
    } else if (e.key === "Enter" && (e.currentTarget.tagName !== "TEXTAREA" || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      const part = partDate(e.currentTarget);
      if (part) inline.refuse(part);
      else inline.commit(e.currentTarget.value, true);
    }
  };
  /** Leaving the field saves it, except when the whole window lost the focus (another tab or app): the
   *  input gets the focus back when the window does, with what was typed. The phone and the e-mail are
   *  put back instead, unless the focus went to their own Save or Cancel. */
  const left = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!inline || !document.hasFocus()) return;
    if (confirmsSave(field)) {
      const to = e.relatedTarget;
      if (to instanceof Node && e.currentTarget.closest("[data-inline-field]")?.contains(to)) return;
      inline.cancel(false);
      return;
    }
    const part = partDate(e.currentTarget);
    if (part) inline.refuse(part);
    else inline.commit(e.currentTarget.value);
  };
  /** What a one-line input and a long text share. */
  const typed = {
    id,
    value: text,
    disabled: locked || disabled,
    invalid,
    autoFocus: Boolean(inline),
    "aria-describedby": describedById,
    onKeyDown: keys,
    onBlur: inline ? left : undefined,
    ...labelled,
  };
  const input = (props: React.ComponentProps<typeof Input>) => (
    <Input
      {...typed}
      autoComplete="off"
      // Always the same shape while inline: a spinner coming and going must not swap the input for a
      // new one, which would take the focus back from wherever the person went (autoFocus).
      trailing={inline ? <span className="flex">{disabled && <Spinner />}</span> : undefined}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      {...props}
      className={cn(inline && "text-sm", props.className)}
    />
  );
  /** A phone, its +1 fixed in front: the rep types the ten digits after it (ACL-653). */
  const phone = (props: Omit<React.ComponentProps<typeof UsPhoneInput>, "value" | "onChange"> = {}) => (
    <UsPhoneInput
      {...typed}
      trailing={inline ? <span className="flex">{disabled && <Spinner />}</span> : undefined}
      onChange={onChange}
      {...props}
      className={cn(inline && "text-sm", props.className)}
    />
  );
  const choice = (options: readonly { key: string; label: string }[], none: string) => {
    // A stored key an admin has since taken off the list stays pickable, so opening and saving does
    // not quietly change it.
    const gone = text && !options.some((o) => o.key === text) ? [{ key: text, label: text }] : [];
    return (
      <Select
        value={text || NOT_SET}
        defaultOpen={Boolean(inline)}
        disabled={locked || disabled}
        onValueChange={(v) => {
          const next = v === NOT_SET ? "" : v;
          chosen.current = true;
          onChange(next);
          inline?.commit(next, true);
        }}
        onOpenChange={(open) => {
          if (open) {
            // Each opening starts afresh, so a pick the server refused can still be put back.
            chosen.current = false;
            pointerOutside.current = false;
          } else if (inline && !chosen.current) {
            // Escape, or the option already picked chosen again: the value, focused.
            inline.cancel(!pointerOutside.current);
          }
        }}
      >
        <SelectTrigger
          id={id}
          aria-label={field.title}
          aria-describedby={describedById}
          invalid={invalid}
          className={cn(inline && "text-sm")}
          // With the menu closed (after a refused pick), Escape puts the value back.
          onKeyDown={escapeCancels}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent onPointerDownOutside={() => (pointerOutside.current = true)}>
          <SelectItem value={NOT_SET}>{none}</SelectItem>
          {[...options, ...gone].map((o) => (
            <SelectItem key={o.key} value={o.key}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };
  /** A day picked from the month (ACL-577). Inline it opens as the field does and a pick saves it, as a
   *  choice does; closing it with nothing picked puts the value back. An accident or a birthday stops at
   *  today. */
  const day = () => {
    const latest = latestDay(field);
    return (
      <DatePicker
        id={id}
        value={text}
        max={latest}
        defaultOpen={Boolean(inline)}
        disabled={locked || disabled}
        invalid={invalid}
        clearable={!field.required}
        // Named by its title and the day it holds.
        aria-labelledby={inline ? `${id}-label ${id}` : undefined}
        aria-describedby={describedById}
        className={cn(inline && "text-sm")}
        // With the month closed (after a refused pick), Escape puts the value back.
        onKeyDown={escapeCancels}
        onChange={(next) => {
          chosen.current = true;
          onChange(next);
          inline?.commit(next, true);
        }}
        onOpenChange={(open) => {
          if (open) {
            chosen.current = false;
            pointerOutside.current = false;
          } else if (inline && !chosen.current) {
            inline.cancel(!pointerOutside.current);
          }
        }}
        onPointerDownOutside={() => (pointerOutside.current = true)}
      />
    );
  };
  /** Cancel, and Save where the field saves on it. A press keeps the focus where it is (no blur
   *  first, which would put the phone back before Save is clicked; Safari does not focus a button). */
  const actions = (withSave: boolean) =>
    inline && (
      <div className="flex justify-end gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => inline.cancel()}
          disabled={disabled}
        >
          Cancel
        </Button>
        {withSave && (
          <Button
            type="button"
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => inline.commit(undefined, true)}
            loading={disabled}
          >
            Save
          </Button>
        )}
      </div>
    );
  /** The phone and the e-mail: their input, with Save and Cancel under it. */
  const confirmed = (props: React.ComponentProps<typeof Input>) => (
    <div data-inline-field className="space-y-2">
      {input(props)}
      {actions(true)}
    </div>
  );

  if (field.system && field.key === "state") {
    return choice(STATE_CHOICES, "Not set");
  }
  if (field.system && (field.key === "first_name" || field.key === "last_name")) {
    return input({ maxLength: DETAIL_LIMITS.name });
  }
  if (field.system && field.key === "phone") {
    return (
      <div data-inline-field className="space-y-2">
        {phone()}
        {actions(true)}
      </div>
    );
  }
  if (inline && confirmsSave(field)) {
    return confirmed({ type: "email", maxLength: maxLength(field) });
  }
  switch (field.type) {
    case "long_text":
      return (
        <Textarea
          {...typed}
          rows={4}
          maxLength={maxLength(field)}
          className="font-sans text-sm"
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "number":
      return input({ inputMode: "decimal" });
    case "email":
      return input({ type: "email", maxLength: maxLength(field) });
    case "phone":
      return phone();
    case "date":
      return day();
    case "datetime":
      return input({ type: "datetime-local" });
    case "boolean":
      return choice([{ key: "yes", label: "Yes" }, { key: "no", label: "No" }], "Not set");
    case "dropdown":
      return choice(field.config.options ?? [], "Not set");
    case "multi_select": {
      const keysPicked = Array.isArray(draft) ? draft : [];
      return (
        <div
          className={cn("space-y-2", inline && "rounded-md border border-border bg-surface p-2.5")}
          onKeyDown={escapeCancels}
        >
          <div
            role="group"
            aria-labelledby={`${id}-label`}
            aria-describedby={describedById}
            className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-text"
          >
            {(field.config.options ?? []).map((o, i) => (
              <label key={o.key} className="inline-flex items-center gap-1.5">
                <input
                  // The first box takes the focus when the field opens.
                  id={i === 0 ? id : undefined}
                  type="checkbox"
                  className="accent-accent-9"
                  disabled={locked || disabled}
                  checked={keysPicked.includes(o.key)}
                  onChange={(e) =>
                    onChange(e.target.checked ? [...keysPicked, o.key] : keysPicked.filter((k) => k !== o.key))
                  }
                />
                {o.label}
              </label>
            ))}
          </div>
          {actions(true)}
        </div>
      );
    }
    case "reference": {
      // The draft is the id; what it reads as is the name picked here, else the stored one's.
      const chosenRef: ReferenceChoice | null = text
        ? { id: text, label: pickedLabel || (String(field.value ?? "") === text ? referenceText(field) : null) || text }
        : null;
      if (locked) return input({ value: chosenRef?.label ?? "", placeholder: "Not set" });
      return (
        // Escape closes the picker's own list first; with it closed, Escape puts the value back.
        <div className="space-y-2" onKeyDown={escapeCancels}>
          <ReferencePicker
            id={id}
            aria-describedby={describedById}
            entity={entity}
            fieldKey={field.key}
            target={field.config.target}
            value={chosenRef}
            onChange={onPick}
            allowNone={!(field.required && field.value !== null && field.value !== undefined)}
            invalid={invalid}
          />
          {actions(false)}
        </div>
      );
    }
    default:
      return input({ maxLength: maxLength(field) });
  }
}
